import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import {
  customFieldDe,
  MOTIVO_RETIRADA_LABEL,
  UBICACION_LABEL,
  type Dispositivo,
  type MotivoRetirada,
  type TipoUbicacion,
} from '@/lib/dispositivos';

/**
 * Retirar de una casa (o de todas). El motivo lleva efectos: avería manda el
 * aparato a cuarentena, robo o pérdida a perdido, el resto a stock usado. Se
 * dice antes de confirmar, junto con qué custom field se va a vaciar.
 */
interface Props {
  abierto: boolean;
  dispositivo: Dispositivo;
  onCerrar: () => void;
  onGuardar: (datos: Record<string, unknown>) => Promise<void>;
}

const MOTIVOS = Object.keys(MOTIVO_RETIRADA_LABEL) as MotivoRetirada[];
const UBICACIONES = Object.keys(UBICACION_LABEL) as TipoUbicacion[];

function destinoDe(motivo: MotivoRetirada): string {
  if (motivo === 'averia') return 'cuarentena, hasta que alguien lo marque como revisado';
  if (motivo === 'robo_perdida') return 'perdido';
  return 'stock (usado)';
}

export default function ModalRetirar({ abierto, dispositivo: d, onCerrar, onGuardar }: Props) {
  const hoy = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
  const [listingId, setListingId] = useState(d.instalaciones.length === 1 ? d.instalaciones[0].listingId : '');
  const [motivo, setMotivo] = useState<MotivoRetirada>('cambio_casa');
  const [ubicacionTipo, setUbicacionTipo] = useState<TipoUbicacion>('oficina');
  const [ubicacionNombre, setUbicacionNombre] = useState('');
  const [fecha, setFecha] = useState(hoy);
  const [nota, setNota] = useState('');
  const [sinHostify, setSinHostify] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const quedanCasas = listingId ? d.instalaciones.length - 1 : 0;
  const cambiaEstado = quedanCasas === 0;
  const pideUbicacion = cambiaEstado && motivo !== 'robo_perdida';
  const inst = d.instalaciones.find((i) => i.listingId === listingId);
  const campo = customFieldDe(d.tipo, inst?.rol);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      await onGuardar({
        listingId: listingId || undefined,
        motivo,
        fecha,
        nota: nota.trim() || undefined,
        ubicacion: pideUbicacion ? { tipo: ubicacionTipo, ...(ubicacionNombre.trim() ? { nombre: ubicacionNombre.trim() } : {}) } : undefined,
        sinHostify: sinHostify || undefined,
      });
      onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={abierto} onClose={onCerrar} title="Retirar">
      <form onSubmit={enviar} className="space-y-4">
        {d.instalaciones.length > 1 && (
          <label className="block">
            <span className="label">De qué casa</span>
            <select className="input mt-1" value={listingId} onChange={(e) => setListingId(e.target.value)}>
              <option value="">De todas ({d.instalaciones.length})</option>
              {d.instalaciones.map((i) => (
                <option key={i.listingId} value={i.listingId}>{i.nickname}{i.principal ? ' (principal)' : ''}</option>
              ))}
            </select>
          </label>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label">Motivo *</span>
            <select className="input mt-1" value={motivo} onChange={(e) => setMotivo(e.target.value as MotivoRetirada)}>
              {MOTIVOS.map((m) => (
                <option key={m} value={m}>{MOTIVO_RETIRADA_LABEL[m]}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">Fecha</span>
            <input type="date" className="input mt-1" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </label>
        </div>

        {pideUbicacion && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="label">Dónde queda</span>
              <select className="input mt-1" value={ubicacionTipo} onChange={(e) => setUbicacionTipo(e.target.value as TipoUbicacion)}>
                {UBICACIONES.map((u) => (
                  <option key={u} value={u}>{UBICACION_LABEL[u]}</option>
                ))}
              </select>
            </label>
            {ubicacionTipo === 'con_empleado' && (
              <label className="block">
                <span className="label">Con quién *</span>
                <input className="input mt-1" required value={ubicacionNombre} onChange={(e) => setUbicacionNombre(e.target.value)} />
              </label>
            )}
          </div>
        )}

        <label className="block">
          <span className="label">Nota</span>
          <textarea className="input mt-1" rows={2} value={nota} onChange={(e) => setNota(e.target.value)} placeholder={motivo === 'averia' ? 'Qué falla; acuérdate de apuntar también la incidencia' : ''} />
        </label>

        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
          <AlertTriangle size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800 space-y-1">
            <p className="font-medium">Qué va a pasar</p>
            <ul className="list-disc list-inside space-y-0.5">
              {campo ? (
                <li>Se vacía el custom field {campo} en Hostify si sigue apuntando a este aparato; si alguien ya puso otro, no se toca.</li>
              ) : (
                <li>Sin custom field en Hostify: solo cambia el inventario.</li>
              )}
              {cambiaEstado ? <li>Pasa a {destinoDe(motivo)}.</li> : <li>Sigue instalado en {quedanCasas} casa{quedanCasas === 1 ? '' : 's'} más.</li>}
              {motivo === 'averia' && cambiaEstado && <li>En cuarentena no se puede volver a instalar hasta marcarlo como revisado.</li>}
            </ul>
          </div>
        </div>

        {campo && (
          <label className="flex items-start gap-2 text-xs text-sereno-dark/60">
            <input type="checkbox" className="mt-0.5" checked={sinHostify} onChange={(e) => setSinHostify(e.target.checked)} />
            <span>Sin vaciar en Hostify (solo si está caído).</span>
          </label>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCerrar} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={guardando} className="btn-danger disabled:opacity-50">
            {guardando ? 'Guardando…' : 'Retirar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
